import { useCallback, useEffect, useRef } from 'react';
import {
  getUID,
  createSessionContextEvent,
  getGaIds,
  getNormalizedPageTitle,
  getRawGaSessionCookie,
  syntheticEventHubEvent
} from '../analyticsUtils';
import { useAuth } from 'react-oidc-context';
import { datadogRum } from '@/lib/DatadogService';
import { runAfterCoreContent } from '@/utils/coreContentReady';

const intervalInSeconds = 5;
const SESSION_CONTEXT_SENT_KEY = '_pli_session_context_sent';

// GTM system events can carry DOM elements (e.g. gtm.element on gtm.formInteract);
// React-managed nodes make JSON.stringify throw on circular fiber references, which
// would poison the whole batch. Serialize defensively instead.
const safeStringifyEvent = (event: object) => {
  const seen = new WeakSet();
  return JSON.stringify(event, (_key, value) => {
    if (value && typeof value === 'object') {
      if (typeof Node !== 'undefined' && value instanceof Node) {
        return value instanceof Element
          ? value.tagName.toLowerCase() + (value.id ? `#${value.id}` : '')
          : '[dom]';
      }
      if (seen.has(value)) return '[circular]';
      seen.add(value);
    }
    return typeof value === 'function' ? undefined : value;
  });
};

// Send events to EventHubs reading window.eventHubDataLayer array every N seconds
export const useEventHubDataLayerConsumer = () => {
  const lastIndexProcessed = useRef<number>(0);
  const sessionContextSent = useRef(false);
  const pageLoadTime = useRef(Date.now());
  const failedFlushAttempts = useRef(0);
  const deliveryStallReported = useRef(false);
  const auth = useAuth();

  // Delivery watchdog: sendBeacon cannot report server rejections, but a backlog that
  // never drains makes every downstream failure mode visible. Reported once per stall.
  const reportDeliveryStallIfNeeded = useCallback((backlog: number) => {
    if (deliveryStallReported.current) return;
    if (failedFlushAttempts.current < 3 && backlog < 50) return;

    deliveryStallReported.current = true;
    datadogRum.addError(new Error('eventhub_delivery_stalled'), {
      backlog,
      failedFlushAttempts: failedFlushAttempts.current
    });
  }, []);
  const userId =
    (auth?.user?.profile?.IndividualSK as string)?.length > 0
      ? (auth.user?.profile?.IndividualSK as string)
      : '000000';

  // Cookies are read per call: GA sets them asynchronously after load, so values
  // captured once at mount could stay 'unknown' for the whole session.
  const getSessionInfo = useCallback(() => {
    const gaCookie = getRawGaSessionCookie();
    const { clientId, sessionId, sessionNumber } = getGaIds();

    return {
      clientId,
      sessionId,
      sessionNumber,
      sessionGuid: gaCookie ? `${gaCookie}.${userId}` : 'NoSession'
    };
  }, [userId]);

  // `sendBeacon` ensures data is sent without being blocked by browser unload restrictions.
  const sendEvents = useCallback((events: string[]) => {
    const dataBlob = new Blob([JSON.stringify(events)], { type: 'application/json' });
    return navigator.sendBeacon(
      `${import.meta.env.VITE_CONTENT_DELIVERY_API}/api/client/event-hub/events`,
      dataBlob
    );
  }, []);

  const sendSessionContext = useCallback(() => {
    const { sessionGuid } = getSessionInfo();
    const sessionStorageKey = `${SESSION_CONTEXT_SENT_KEY}_${sessionGuid}`;
    const alreadySent = sessionStorage.getItem(sessionStorageKey);

    if (!alreadySent && !sessionContextSent.current) {
      // Queue through the shared push path so the event gets the page snapshot and
      // unique id, and is sent exactly once by processEvents (a direct send here
      // would duplicate it on the first flush).
      syntheticEventHubEvent(createSessionContextEvent());
      sessionStorage.setItem(sessionStorageKey, 'true');
      sessionContextSent.current = true;
    }
  }, [getSessionInfo]);

  const processEvents = useCallback(() => {
    const currentLastIndex = window.eventHubDataLayer?.length ?? 0;
    if (currentLastIndex <= lastIndexProcessed.current) return;

    const eventsToBeProcessed = window.eventHubDataLayer.slice(
      lastIndexProcessed.current,
      currentLastIndex
    );

    try {
      const { clientId, sessionId, sessionNumber, sessionGuid } = getSessionInfo();
      const analyticsEventHash = getUID();
      const analyticsSentEventTimestamp = new Date().getTime();
      const enrichedEvents = eventsToBeProcessed.map((event, index) => {
        const newEvent = {
          ...event,
          client_id: clientId || 'unknown',
          ga_session_id: sessionId || 'unknown',
          ga_session_number: sessionNumber?.toString() || '1',
          analytics_event_hash: analyticsEventHash,
          analytics_sent_event_timestamp: analyticsSentEventTimestamp,
          sessionGuid: sessionGuid,
          // Push-time values win; flush-time values are the fallback for events that only
          // arrive via the loadExternals.js dataLayer mirror (gtm.*, direct dataLayer pushes).
          // Fallback engagement is time-since-load (legacy semantics), push-time is
          // time-since-previous-event (GA4 semantics).
          engagement_time_msec: event.engagement_time_msec ?? Date.now() - pageLoadTime.current,
          page_location: event.page_location ?? window.location.href,
          page_referrer: event.page_referrer ?? document.referrer,
          page_title: event.page_title ?? getNormalizedPageTitle(),
          dataSource: 'FrontEnd'
        };

        window.eventHubDataLayer[lastIndexProcessed.current + index] = newEvent;

        return safeStringifyEvent(newEvent);
      });

      // sendBeacon returns false when it cannot queue the payload; keep the index
      // unchanged so the same batch is retried on the next flush.
      if (sendEvents(enrichedEvents)) {
        lastIndexProcessed.current = currentLastIndex;
        failedFlushAttempts.current = 0;
        deliveryStallReported.current = false;
      } else {
        failedFlushAttempts.current += 1;
        reportDeliveryStallIfNeeded(eventsToBeProcessed.length);
      }
    } catch (error) {
      // Keep lastIndexProcessed unchanged so the batch is retried on the next flush
      failedFlushAttempts.current += 1;
      const err =
        error instanceof Error ? error : new Error(`event hub flush error: ${String(error)}`);
      datadogRum.addError(err, {
        customMessage: 'event hub flush error',
        thrownValue: String(error)
      });
      reportDeliveryStallIfNeeded(eventsToBeProcessed.length);
    }
  }, [getSessionInfo, reportDeliveryStallIfNeeded, sendEvents]);

  useEffect(() => {
    let timerInterval: number | undefined;
    const cancelDeferredStart = runAfterCoreContent(() => {
      sendSessionContext();
      processEvents();
      timerInterval = window.setInterval(processEvents, intervalInSeconds * 1000);
    });

    // Flush pending events when the page is hidden or unloaded, otherwise events
    // pushed during the last interval window are lost on navigation away.
    const flush = () => processEvents();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelDeferredStart();
      if (timerInterval !== undefined) clearInterval(timerInterval);
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [processEvents, sendSessionContext]);

  return {
    processEvents
  };
};
